import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import jwtDecode from 'jwt-decode';

import dataLayerPushAdmission from '@/utils/mixins/dataLayerPushAdmission';
import {
  LOCAL_STORAGE_CPF,
  LOCAL_STORAGE_URL_TOKEN,
  LOCAL_STORAGE_STATE_PERSIST,
} from '@/utils/constants/checkin.constants';

import {
  PATIENT_WITHOUT_MDMID,
  UNEXPIRED_TOKEN,
  SCHEDULED_ADMISSION_HAS_NO_EXAM,
  IS_SERVICE_UNAVAILABLE_ON_MDM,
  INVALID_TOKEN,
} from '@/utils/constants/datalayerMessage.constants';

import datalayerErrorMessage from '@/utils/helpers/message/datalayerErrorMessage.js';

const checkinMixins = {
  name: 'checkinMixins',
  computed: {
    ...mapState({
      isElegibleToBypassToPayment: ({ global }) => global.isElegibleToBypassToPayment,
      admission: ({ currentAdmission }) => currentAdmission.data,
      urlToken: ({ validation }) => validation.data.urlToken,
      mdmId: ({ mdm }) => mdm.data.id,
      cpfSearched: ({ cpfSearched }) => cpfSearched.data.cpf,
      isAdmissionAuthorized: ({ global }) => global.isAdmissionAuthorized,
      brandId: ({ brandUnit }) => brandUnit.data.currentBrand.brandId,
    }),
    ...mapGetters({
      haveScheduledExams: 'haveScheduledExams',
      hasCompletedFlow: 'global/hasCompletedFlow',
      isTrackingActiveInUnit: 'isTrackingActiveInUnit',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
      isT2FlowAllowed: 'global/isT2FlowAllowed',
      selectedBrand: 'selectedBrand',
      enumUnitSelected: 'enumUnitSelected',
    }),
  },
  data() {
    return {
      isServiceUnavailableOnMdm: false,
    };
  },
  mixins: [dataLayerPushAdmission],
  methods: {
    ...mapActions([
      'getMdmPatientByCpf',
      'getAdmissionAndMdmIdsByCpf',
      'getAdmissionById',
      'checkAndSetT2Flow',
      'getIsIncompany',
      'global/setIsScheduledAdmission',
      'global/checkPaymentAuthorization',
      'global/updateFlowSteps',
      'updateAdmissionStatus',
      'resetStatesVuexPersist',
    ]),
    ...mapMutations({
      setCpfSearched: 'SET_CPF_SEARCHED',
      setAdmission: 'SET_ADMISSION_DATA',
      setAtUnit: 'global/SET_AT_UNIT',
      setAdmissionId: 'SET_ADMISSION_ID',
      setMdmIdPatient: 'SET_MDM_ID_PATIENT',
    }),
    inputCPFAutofiller() {
      const userCpf = sessionStorage.getItem(LOCAL_STORAGE_CPF);
      if (userCpf) {
        this.cpf = userCpf;
      }
    },
    unmaskedCpf(cpf = '') {
      return cpf.replace(/[^\d]/g, '');
    },
    /**
     * @method startCheckin
     * @description Starts check-in process:
     * Pushes datalayer event and gets admission/mdm restricted data
     */
    async startCheckin(cpf) {
      this.setCpfSearched(cpf);
      const recognizePatient = await this.getAdmissionAndMdmIdsByCpf({
        cpf,
        token: this.urlToken,
      });

      const NOT_FOUND_STATUS = '404';
      const errorStatus = recognizePatient?.errors?.mdmError?.status;

      if (errorStatus && String(errorStatus) !== NOT_FOUND_STATUS) {
        this.isServiceUnavailableOnMdm = true;
      }

      const admissionId = this.admission.id;
      const admissionIdStorage = localStorage.getItem('admissionId');

      if (!admissionId || admissionIdStorage !== admissionId) {
        localStorage.removeItem('admissionId');
        this.resetStatesVuexPersist();
      }

      this.getIsIncompany();

      const admissionStatusCheckinWaiting = this.admission.status === 'CHECK_IN_WAITING';

      if (!admissionStatusCheckinWaiting) {
        await this['global/checkPaymentAuthorization'](admissionId);
      }

      if (admissionId && !this.hasCompletedFlow.isStatusDone && !admissionStatusCheckinWaiting) {
        await this.fetchPatientAdmissionData();

        if (this.admission.checkInDone) {
          await this.updateAdmissionStatus('check-in-done');
        }
      }

      this.admissionFlowTypeController(this.hasCompletedFlow.isStatusDone);
    },

    async validateExistingToken() {
      function removeToken() {
        sessionStorage.removeItem('user');
        return false;
      }
      const savedData = localStorage?.getItem(LOCAL_STORAGE_STATE_PERSIST);

      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (this.cpfSearched !== parsedData.registrationData.cpf) {
          removeToken();
        }
      }
      const savedToken = sessionStorage?.getItem('user');
      if (savedToken) {
        const decodedToken = jwtDecode(savedToken);
        const isTokenExpired = decodedToken.exp < new Date() / 1000;
        if (!isTokenExpired) {
          return true;
        }
      }
      removeToken();
    },
    /**
     * @method admissionFlowTypeController
     * @description Controls which flow patient will follow [Admission Done/ Without Schedule/ With Schedule]
     */
    async admissionFlowTypeController(isAdmissionDone) {
      const { medicalOrders, source, id: admissionId } = this.admission;
      if (admissionId) {
        // invalida admissão agendada que não tenha nenhum exame
        if (
          source?.origin &&
          source.origin !== 'AUT' &&
          (!medicalOrders?.length || !medicalOrders[0].exams?.length)
        ) {
          return this.goNextStep(
            'check_service_desk',
            datalayerErrorMessage('admissionFlowTypeController', SCHEDULED_ADMISSION_HAS_NO_EXAM),
          );
        }

        localStorage.setItem('admissionId', admissionId);
        isAdmissionDone
          ? await this.admissionStatusDoneFlow()
          : await this.admissionScheduledFlow();

        return;
      }
      this.admissionWithoutScheduleFlow();
    },
    /**
     * @method admissionWithoutScheduleFlow
     * @description No Schedule: calls for admission creation/ mdm data verification
     */
    async admissionWithoutScheduleFlow() {
      const isPatientRegisteredInMdmApi = this.mdmId;

      // Set flag for non-scheduled admissions
      // We check this flag in checkinConclusion to update admission status to ADMISSION_DONE
      this.setAtUnit(true);

      if (this.isServiceUnavailableOnMdm) {
        return await this.goNextStep(
          'service_unavailable',
          datalayerErrorMessage('admissionWithoutScheduleFlow', IS_SERVICE_UNAVAILABLE_ON_MDM),
        );
      }

      const unexpiredToken = await this.validateExistingToken();
      if (unexpiredToken) {
        await this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('admissionWithoutScheduleFlow', UNEXPIRED_TOKEN),
        );
        return;
      }

      isPatientRegisteredInMdmApi
        ? await this.goNextStep('patient_validation_contact_types')
        : await this.goNextStep(
            'check_service_desk',
            datalayerErrorMessage('admissionWithoutScheduleFlow', PATIENT_WITHOUT_MDMID),
          );
    },
    /**
     * @method patientIdentityVerified
     * @description Calls admission data fetcher.
     */
    async patientIdentityVerified() {
      await this.fetchPatientAdmissionData();
      return this.goNextStep('register_patient');
    },

    // Patient Data - MDM/ADMISSION/CPF -------------------------------------------

    /**
     * @method fetchPatientAdmissionData
     * @description Gets admission data
     */
    async fetchPatientAdmissionData() {
      await this.getAdmissionById();
    },

    /**
     * @method admissionStatusDoneFlow
     * @description Fetches patient mdm data in case current unit has tracking.
     */
    async admissionStatusDoneFlow() {
      let lastStep = 'admission_done';
      await this.getAdmissionById();
      await this.getMdmPatientByCpf(this.cpfSearched);

      const isSameDay = this.$moment(this.admission.admissionDate).isSame(new Date(), 'day');
      const { status } = this.hasCompletedFlow;

      // needs to check the admission origin !=== 'AUT'
      if (this.admission.medicalOrders && !!this.admission.medicalOrders.length) {
        await this['global/setIsScheduledAdmission'](true);
      }

      await this.checkAndSetT2Flow();
      this.getIsIncompany();

      const paymentStatus = this.admission.payments[0]?.status;

      if (paymentStatus === 'PAID') {
        this['global/updateFlowSteps']([8, 2]);
      }

      const isInCompany = this.admission.source.origin === 'CEV';
      if (
        (this.isT2FlowAllowed && paymentStatus === 'PAID') ||
        ((this.isAdmissionAuthorized || isInCompany) && status !== 'ADMISSION_DONE')
      ) {
        return this.goNextStep('checkin_conclusion_simple');
      }
      if (this.isElegibleToBypassToPayment) {
        return this.goNextStep('payment_resume');
      }

      if (this.isT2FlowAllowed || this.isAdmissionAuthorized) {
        const hoursDifference = await this.returnHoursDifference(new Date(), this.examDate);
        if (hoursDifference <= 2 && status !== 'ADMISSION_DONE') {
          return this.goNextStep('end_flow_success');
        }
        return this.goNextStep('queue_end_flow');
      }

      if (this.isTrackingActiveInUnit) {
        lastStep =
          status === 'ADMISSION_DONE' && paymentStatus === 'PAID'
            ? 'admission_done'
            : 'end_flow_success';
        await this.getMdmPatientByCpf(this.cpfSearched);
      }

      if (this.admission.admissionDate) {
        isSameDay ? this.goNextStep(lastStep) : this.goNextStep('admission_done');
      } else {
        this.goNextStep(lastStep);
      }
    },
    // Scheduled / No Schedule / Checkin done (redirect to end) Flow --------------
    /**
     * @method admissionScheduledFlow
     * @description Admission scheduled method: verifies url token existance/ sends to identity confirmation
     */
    async admissionScheduledFlow() {
      const tokenParamExists = this.urlToken;

      if (tokenParamExists) {
        const isTokenValid = this.tokenValidator();
        if (isTokenValid) {
          await this['global/setIsScheduledAdmission'](true);
          await this.patientIdentityVerified();
          return;
        }

        await this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('admissionScheduledFlow', INVALID_TOKEN),
        );
        return;
      }

      const unexpiredToken = await this.validateExistingToken();

      unexpiredToken
        ? await this.goNextStep(
            'check_service_desk',
            datalayerErrorMessage('admissionScheduledFlow', UNEXPIRED_TOKEN),
          )
        : await this.goNextStep('patient_validation_contact_types');
    },
    /**
     * @method inputAllowNumbersOnly
     * @description Prohibits user from inserting letters and symbols.
     */
    inputAllowNumbersOnly(event) {
      if (
        event.keyCode > 31 &&
        (event.keyCode < 48 || event.keyCode > 57) &&
        event.keyCode !== 46
      ) {
        event.preventDefault();
      }
    },
    // Url Admission Token -------------------------------------------------------
    /**
     * @method tokenValidator
     * @param {string} admissionId
     * @description Validates if token from url is included in admission id
     * @returns truthy if token is equal to admission first digits
     */
    tokenValidator() {
      const admissionId = this.admission.id;

      return admissionId ? this.urlToken === admissionId.split('-')[0] : false;
    },
    async bypassLoginUsingUrlToken() {
      const { token } = this.$route?.query;
      const admissionOnUrl = this.$route.params.admissionID;
      const resetStates = () => {
        this.setAdmissionId('');
        localStorage.removeItem(LOCAL_STORAGE_URL_TOKEN);
        sessionStorage.removeItem('');
      };
      try {
        if (admissionOnUrl && admissionOnUrl.length > 10) {
          this.setAdmissionId(admissionOnUrl);
        } else {
          throw new Error('Invalid Admission');
        }
        if (token) {
          const decodedToken = jwtDecode(token);
          const isTokenExpired = decodedToken.exp < new Date() / 1000;
          if (decodedToken.admissionId === admissionOnUrl && !isTokenExpired) {
            localStorage.setItem(LOCAL_STORAGE_URL_TOKEN, admissionOnUrl);
            sessionStorage.setItem('user', token);
            await this.getAdmissionById();
            if (this.admission.mdmIdPatient === decodedToken.token) {
              this.setMdmIdPatient(this.admission.mdmIdPatient);
              await this['global/setIsScheduledAdmission'](true);
              await this['global/checkPaymentAuthorization'](admissionOnUrl);

              if (this.admission.checkInDone) {
                await this.checkAndSetT2Flow();
                this.isAdmissionAuthorized || this.isT2FlowAllowed
                  ? this.goNextStep('checkin_conclusion_simple')
                  : this.goNextStep('end_queue_step');
              } else {
                this.goNextStep('register_patient');
              }
            } else {
              throw new Error('Invalid MDM patient');
            }
          } else {
            throw new Error('Invalid data on URL');
          }
        } else {
          throw new Error('Invalid token');
        }
      } catch (err) {
        resetStates();
      }
    },
  },
  mounted() {
    if (this.$route.query && this.$route.query.token) {
      this.bypassLoginUsingUrlToken();
    }
  },
};

export default checkinMixins;
